import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Save, Coins } from "lucide-react";
import ImageUploader from "../components/ImageUploader";
import { useAdmin } from "../context/AdminContext";

// ============ shared modal / field / css ============
export function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-3xl p-6 md:p-8 w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-black">{title}</h3>
          <button onClick={onClose} className="p-1 text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
        </div>
        <style>{`.input { width: 100%; height: 42px; border: 1px solid #d4d4d4; border-radius: 12px; padding: 0 12px; font-size: 14px; background: white; }
                 .input:focus { outline: none; border-color: #171717; }
                 textarea.input { padding: 10px 12px; height: auto; }
                 .btn-primary { height: 40px; padding: 0 20px; border-radius: 999px; background: #FF5A1F; color: white; font-weight: 700; font-size: 13px; }
                 .btn-primary:hover { background: #171717; }
                 .btn-primary:disabled { opacity: 0.6; }
                 .btn-secondary { height: 40px; padding: 0 20px; border-radius: 999px; border: 1px solid #d4d4d4; font-weight: 600; font-size: 13px; }`}</style>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div className="mt-3">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-600 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

// ============ Site Settings ============
export function SettingsTab({ settings, api, onChange }) {
  const { token } = useAdmin();
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);

  React.useEffect(() => { setForm(settings || {}); }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site-settings", {
        rating: parseFloat(form.rating) || 0,
        happy_customers: form.happy_customers,
        products_count: form.products_count,
        cities_served: form.cities_served,
        since_year: form.since_year,
        hero_lifestyle_image: form.hero_lifestyle_image,
        trust_badges: form.trust_badges,
        points_per_rupee: parseFloat(form.points_per_rupee) || 0,
        redeem_value_per_point: parseFloat(form.redeem_value_per_point) || 0,
        min_redeem_points: parseInt(form.min_redeem_points) || 0,
      });
      toast.success("Settings saved");
      onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-neutral-500">Loading...</div>;

  return (
    <div className="max-w-3xl" data-testid="admin-settings-tab">
      <div className="bg-white border border-neutral-200 rounded-3xl p-6 md:p-8">
        <h3 className="font-display text-xl font-black">Social Proof Numbers</h3>
        <p className="text-sm text-neutral-500 mt-1">Displayed on the dark bar right below the hero.</p>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Field label="Star rating (e.g. 4.8)">
            <input type="number" step="0.1" value={form.rating || ""} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="input" data-testid="ss-rating" />
          </Field>
          <Field label="Happy customers (display)">
            <input value={form.happy_customers || ""} onChange={(e) => setForm({ ...form, happy_customers: e.target.value })} placeholder="5,00,000+" className="input" data-testid="ss-customers" />
          </Field>
          <Field label="Products count">
            <input value={form.products_count || ""} onChange={(e) => setForm({ ...form, products_count: e.target.value })} placeholder="250+" className="input" data-testid="ss-products" />
          </Field>
          <Field label="Cities served">
            <input value={form.cities_served || ""} onChange={(e) => setForm({ ...form, cities_served: e.target.value })} placeholder="20+" className="input" data-testid="ss-cities" />
          </Field>
          <Field label="Since (year)">
            <input value={form.since_year || ""} onChange={(e) => setForm({ ...form, since_year: e.target.value })} placeholder="2013" className="input" data-testid="ss-year" />
          </Field>
        </div>

        <h3 className="font-display text-xl font-black mt-8">Hero Lifestyle Image</h3>
        <p className="text-sm text-neutral-500 mt-1">Full-cover image on the homepage hero (right side).</p>
        <Field label="Image URL">
          <input value={form.hero_lifestyle_image || ""} onChange={(e) => setForm({ ...form, hero_lifestyle_image: e.target.value })} className="input font-mono text-xs" data-testid="ss-hero-img" />
          <div className="mt-2"><ImageUploader token={token} onUploaded={(url) => setForm({ ...form, hero_lifestyle_image: url })} /></div>
        </Field>
        {form.hero_lifestyle_image && (
          <div className="mt-3 aspect-[16/9] max-w-md rounded-2xl overflow-hidden bg-neutral-100">
            <img src={form.hero_lifestyle_image} alt="Preview" className="w-full h-full object-cover" />
          </div>
        )}

        <h3 className="font-display text-xl font-black mt-8">Loyalty / Rewards Program</h3>
        <p className="text-sm text-neutral-500 mt-1">Configure how customers earn and redeem loyalty points.</p>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <Field label="Points earned per ₹1 spent">
            <input type="number" step="0.01" value={form.points_per_rupee ?? 0.1} onChange={(e) => setForm({ ...form, points_per_rupee: e.target.value })} className="input" data-testid="ss-ppr" />
          </Field>
          <Field label="₹ value per point (redeem)">
            <input type="number" step="0.1" value={form.redeem_value_per_point ?? 1} onChange={(e) => setForm({ ...form, redeem_value_per_point: e.target.value })} className="input" data-testid="ss-vpp" />
          </Field>
          <Field label="Minimum points to redeem">
            <input type="number" value={form.min_redeem_points ?? 100} onChange={(e) => setForm({ ...form, min_redeem_points: e.target.value })} className="input" data-testid="ss-min" />
          </Field>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Current setup: earn <b>{form.points_per_rupee || 0.1}</b> pts per ₹1 spent · redeem at <b>₹{form.redeem_value_per_point || 1}</b>/pt · minimum <b>{form.min_redeem_points || 100}</b> pts required. Redemption capped at 50% of order value.
        </p>

        <div className="mt-8 flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-settings-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Subcategories ============
const emptySub = { slug: "", name: "", category: "", order: 100, active: true };

export function SubcategoriesTab({ subcategories, categories, api, onChange }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptySub);

  const openCreate = () => { setForm({ ...emptySub, category: categories[0]?.slug || "" }); setCreating(true); setEditing(null); };
  const openEdit = (s) => { setForm({ ...s }); setEditing(s.slug); setCreating(false); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    try {
      if (creating) { await api.post("/admin/subcategories", { ...form, order: Number(form.order) || 100 }); toast.success("Sub-category created"); }
      else { const { slug: _s, ...upd } = form; await api.patch(`/admin/subcategories/${editing}`, { ...upd, order: Number(upd.order) || 100 }); toast.success("Updated"); }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await api.delete(`/admin/subcategories/${s.slug}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail || e.message }); }
  };

  // Group by parent category
  const grouped = categories.reduce((acc, c) => {
    acc[c.slug] = { cat: c, items: subcategories.filter((s) => s.category === c.slug) };
    return acc;
  }, {});

  return (
    <div data-testid="admin-subcategories-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{subcategories.length} sub-categories · click toggle to show/hide on site</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-sub-btn">
          <Plus className="w-4 h-4" /> Add Sub-Category
        </button>
      </div>

      <div className="space-y-6">
        {Object.values(grouped).map(({ cat, items }) => (
          <div key={cat.slug} className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
            <div className="px-6 py-4 bg-neutral-50 flex items-center justify-between">
              <div>
                <div className="font-display font-black text-lg">{cat.name}</div>
                <div className="text-xs text-neutral-500 font-mono">{cat.slug}</div>
              </div>
              <div className="text-xs text-neutral-500">{items.length} sub-categories</div>
            </div>
            {items.length === 0 ? (
              <div className="p-6 text-center text-neutral-500 text-sm">No sub-categories yet.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {items.map((s) => (
                    <tr key={s.slug} className="border-t border-neutral-100" data-testid={`admin-sub-${s.slug}`}>
                      <td className="p-4 font-medium">{s.name}</td>
                      <td className="p-4 text-xs font-mono text-neutral-500">{s.slug}</td>
                      <td className="p-4 text-right">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={s.active}
                            onChange={async (e) => {
                              try { await api.patch(`/admin/subcategories/${s.slug}`, { active: e.target.checked }); toast.success(e.target.checked ? "Now visible" : "Hidden"); onChange(); }
                              catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                            }}
                            className="accent-[#FF5A1F] w-4 h-4"
                            data-testid={`toggle-sub-${s.slug}`}
                          />
                          <span className="text-xs font-semibold">{s.active ? "Visible" : "Hidden"}</span>
                        </label>
                      </td>
                      <td className="p-4 pr-6 text-right">
                        <div className="inline-flex gap-2">
                          <button onClick={() => openEdit(s)} className="text-xs font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                          <button onClick={() => del(s)} className="text-xs font-semibold text-red-600 hover:underline flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Sub-Category" : "Edit Sub-Category"}>
          <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="sf-name" /></Field>
          <Field label="Slug *"><input value={form.slug} disabled={!creating} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="input disabled:bg-neutral-100" data-testid="sf-slug" /></Field>
          <Field label="Parent Category *">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" data-testid="sf-category">
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Sort order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="input" /></Field>
          <label className="flex items-center gap-2 mt-3">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm">Visible in menu</span>
          </label>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-sub-btn">{creating ? "Create" : "Update"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ UGC ============
const emptyUgc = { title: "", embed_url: "", thumbnail: "", author_handle: "", order: 100, active: true };

export function UGCTab({ ugc, api, onChange }) {
  const { token } = useAdmin();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyUgc);

  const openCreate = () => { setForm(emptyUgc); setCreating(true); setEditing(null); };
  const openEdit = (u) => { setForm({ ...u }); setEditing(u.id); setCreating(false); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    try {
      if (creating) { await api.post("/admin/ugc", { ...form, order: Number(form.order) || 100 }); toast.success("UGC added"); }
      else { await api.patch(`/admin/ugc/${editing}`, { ...form, order: Number(form.order) || 100 }); toast.success("Updated"); }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };
  const del = async (u) => {
    if (!window.confirm(`Delete this post?`)) return;
    try { await api.delete(`/admin/ugc/${u.id}`); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-ugc-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{ugc.length} Instagram post{ugc.length !== 1 ? "s" : ""}</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-ugc-btn">
          <Plus className="w-4 h-4" /> Add Post
        </button>
      </div>

      {ugc.length === 0 ? (
        <div className="bg-neutral-50 rounded-3xl p-10 text-center text-neutral-500">
          No Instagram posts yet. Add the first one — the UGC section will appear on the homepage as soon as you do.
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {ugc.map((u) => (
            <div key={u.id} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
              <div className="aspect-square bg-neutral-100">
                {u.thumbnail ? <img src={u.thumbnail} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">No preview</div>}
              </div>
              <div className="p-4">
                <div className="text-xs text-neutral-500 truncate">{u.embed_url}</div>
                <div className="mt-1 text-sm font-medium line-clamp-1">{u.title || "—"}</div>
                {u.author_handle && <div className="text-xs text-[#FF5A1F] mt-1">@{u.author_handle.replace("@", "")}</div>}
                <div className="mt-3 flex justify-between items-center">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.active ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-500"}`}>{u.active ? "Active" : "Hidden"}</span>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(u)} className="text-xs font-semibold text-[#FF5A1F] hover:underline"><Edit3 className="w-3.5 h-3.5 inline" /></button>
                    <button onClick={() => del(u)} className="text-xs font-semibold text-red-600 hover:underline"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Instagram Post" : "Edit Post"}>
          <Field label="Instagram URL *"><input value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })} placeholder="https://instagram.com/reel/..." className="input font-mono text-xs" data-testid="uf-url" /></Field>
          <Field label="Thumbnail image URL">
            <input value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} placeholder="https://.../thumb.jpg" className="input font-mono text-xs" />
            <div className="mt-2"><ImageUploader token={token} onUploaded={(url) => setForm({ ...form, thumbnail: url })} /></div>
          </Field>
          <Field label="Caption / title"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" /></Field>
          <Field label="Author handle"><input value={form.author_handle} onChange={(e) => setForm({ ...form, author_handle: e.target.value })} placeholder="stealodeal" className="input" /></Field>
          <Field label="Sort order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="input" /></Field>
          <label className="flex items-center gap-2 mt-3">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm">Show on site</span>
          </label>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-ugc-btn">{creating ? "Create" : "Update"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ Coupons ============
const emptyCoupon = { code: "", discount_type: "percent", value: 10, label: "", min_amount: 0, usage_limit: 0, active: true };

export function CouponsTab({ coupons, api, onChange }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyCoupon);

  const openCreate = () => { setForm(emptyCoupon); setCreating(true); setEditing(null); };
  const openEdit = (c) => { setForm({ ...c }); setEditing(c.code); setCreating(false); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    try {
      const payload = { ...form, value: Number(form.value) || 0, min_amount: Number(form.min_amount) || 0, usage_limit: Number(form.usage_limit) || 0 };
      if (creating) { await api.post("/admin/coupons", payload); toast.success("Coupon created"); }
      else { const { code: _c, ...upd } = payload; await api.patch(`/admin/coupons/${editing}`, upd); toast.success("Updated"); }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try { await api.delete(`/admin/coupons/${c.code}`); toast.success("Deleted"); onChange(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-coupons-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{coupons.length} coupon{coupons.length !== 1 ? "s" : ""}</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4" /> Add Coupon
        </button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left p-4">Code</th><th className="text-left p-4">Type</th>
              <th className="text-right p-4">Value</th><th className="text-left p-4">Label</th>
              <th className="text-right p-4">Min ₹</th><th className="text-right p-4">Used / Limit</th>
              <th className="text-center p-4">Active</th><th className="text-right p-4 pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <tr key={c.code} className="border-t border-neutral-100" data-testid={`admin-coupon-${c.code}`}>
                <td className="p-4 font-mono font-bold">{c.code}</td>
                <td className="p-4 text-xs">{c.discount_type}</td>
                <td className="p-4 text-right">{c.discount_type === "percent" ? `${c.value}%` : c.value ? `₹${c.value}` : "—"}</td>
                <td className="p-4 text-xs text-neutral-600">{c.label}</td>
                <td className="p-4 text-right text-xs">{c.min_amount || "—"}</td>
                <td className="p-4 text-right text-xs">{c.used_count || 0} / {c.usage_limit || "∞"}</td>
                <td className="p-4 text-center"><span className={`inline-block w-2 h-2 rounded-full ${c.active ? "bg-green-500" : "bg-neutral-300"}`} /></td>
                <td className="p-4 pr-6 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(c)} className="text-xs font-semibold text-[#FF5A1F] hover:underline"><Edit3 className="w-3.5 h-3.5 inline" /></button>
                    <button onClick={() => del(c)} className="text-xs font-semibold text-red-600 hover:underline"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {coupons.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-neutral-500">No coupons yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Coupon" : "Edit Coupon"}>
          <Field label="Code *"><input value={form.code} disabled={!creating} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="input disabled:bg-neutral-100 uppercase font-mono" data-testid="cpf-code" /></Field>
          <Field label="Discount type">
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="input" data-testid="cpf-type">
              <option value="percent">Percent off</option>
              <option value="flat">Flat rupees off</option>
              <option value="shipping">Free shipping</option>
              <option value="gift">Free gift</option>
            </select>
          </Field>
          <Field label={form.discount_type === "percent" ? "Percentage (0-100)" : "Flat amount ₹"}>
            <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="input" data-testid="cpf-value" />
          </Field>
          <Field label="Display label (e.g. 10% OFF)"><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Minimum order ₹"><input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="input" /></Field>
            <Field label="Usage limit (0 = unlimited)"><input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className="input" /></Field>
          </div>
          <label className="flex items-center gap-2 mt-3">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm">Active</span>
          </label>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-coupon-btn">{creating ? "Create" : "Update"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ Spin Prizes ============
const emptyPrize = { label: "", coupon_code: "", weight: 10, color: "#FF5A1F", active: true };

export function SpinTab({ prizes, coupons, api, onChange }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyPrize);

  const openCreate = () => { setForm({ ...emptyPrize, coupon_code: coupons[0]?.code || "" }); setCreating(true); setEditing(null); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.id); setCreating(false); };
  const close = () => { setCreating(false); setEditing(null); };

  const save = async () => {
    try {
      const payload = { ...form, weight: Number(form.weight) || 1 };
      if (creating) { await api.post("/admin/spin-prizes", payload); toast.success("Prize added"); }
      else { await api.patch(`/admin/spin-prizes/${editing}`, payload); toast.success("Updated"); }
      close(); onChange();
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };
  const del = async (p) => {
    if (!window.confirm(`Remove "${p.label}" from the wheel?`)) return;
    try { await api.delete(`/admin/spin-prizes/${p.id}`); toast.success("Removed"); onChange(); }
    catch { toast.error("Delete failed"); }
  };

  const totalWeight = prizes.reduce((s, p) => s + (p.active ? (p.weight || 1) : 0), 0);

  return (
    <div data-testid="admin-spin-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{prizes.length} prize{prizes.length !== 1 ? "s" : ""} on the wheel</div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="add-prize-btn">
          <Plus className="w-4 h-4" /> Add Prize
        </button>
      </div>
      <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left p-4">Prize</th><th className="text-left p-4">Coupon</th>
              <th className="text-right p-4">Weight</th><th className="text-right p-4">% chance</th>
              <th className="text-center p-4">Colour</th><th className="text-center p-4">Active</th>
              <th className="text-right p-4 pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {prizes.map((p) => (
              <tr key={p.id} className="border-t border-neutral-100" data-testid={`admin-prize-${p.id}`}>
                <td className="p-4 font-medium">{p.label}</td>
                <td className="p-4 font-mono text-xs">{p.coupon_code}</td>
                <td className="p-4 text-right">{p.weight}</td>
                <td className="p-4 text-right text-xs">{totalWeight ? Math.round((p.active ? p.weight : 0) / totalWeight * 100) : 0}%</td>
                <td className="p-4 text-center"><span className="inline-block w-5 h-5 rounded-full border border-neutral-200" style={{ background: p.color }} /></td>
                <td className="p-4 text-center"><span className={`inline-block w-2 h-2 rounded-full ${p.active ? "bg-green-500" : "bg-neutral-300"}`} /></td>
                <td className="p-4 pr-6 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(p)} className="text-xs font-semibold text-[#FF5A1F] hover:underline"><Edit3 className="w-3.5 h-3.5 inline" /></button>
                    <button onClick={() => del(p)} className="text-xs font-semibold text-red-600 hover:underline"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {prizes.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-neutral-500">No prizes yet — add at least 2 to activate the wheel.</td></tr>}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <Modal onClose={close} title={creating ? "Add Prize" : "Edit Prize"}>
          <Field label="Label on wheel *"><input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="10% OFF" className="input" data-testid="pzf-label" /></Field>
          <Field label="Coupon code *">
            <select value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} className="input" data-testid="pzf-coupon">
              <option value="">— select coupon —</option>
              {coupons.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.label}</option>)}
            </select>
          </Field>
          <Field label="Weight (probability, higher = more likely)"><input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="input" /></Field>
          <Field label="Segment colour">
            <div className="flex gap-2 items-center">
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-11 w-14 rounded-xl border border-neutral-300" />
              <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="input font-mono text-xs" />
            </div>
          </Field>
          <label className="flex items-center gap-2 mt-3">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#FF5A1F] w-4 h-4" />
            <span className="text-sm">Active on wheel</span>
          </label>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={close} className="btn-secondary">Cancel</button>
            <button onClick={save} className="btn-primary" data-testid="save-prize-btn">{creating ? "Create" : "Update"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============ Loyalty Accounts ============
export function LoyaltyTab({ api }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [adjusting, setAdjusting] = React.useState(null);
  const [delta, setDelta] = React.useState("");

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/loyalty");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load loyalty data");
    } finally { setLoading(false); }
  };
  React.useEffect(() => { fetch(); /* eslint-disable-next-line */ }, []);

  const submitAdjust = async () => {
    const d = parseInt(delta) || 0;
    if (!d) { toast.error("Enter a non-zero value"); return; }
    try {
      await api.post(`/admin/loyalty/${encodeURIComponent(adjusting)}/adjust`, { delta: d, note: "admin adjust" });
      toast.success(`${d > 0 ? "+" : ""}${d} points`);
      setAdjusting(null); setDelta("");
      fetch();
    } catch (e) { toast.error("Failed", { description: e?.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="admin-loyalty-tab">
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-neutral-500">{rows.length} customer{rows.length !== 1 ? "s" : ""} with loyalty balance</div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-3xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left p-4">Customer</th>
              <th className="text-right p-4">Balance</th>
              <th className="text-right p-4">Earned</th>
              <th className="text-right p-4">Redeemed</th>
              <th className="text-right p-4 pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-10 text-neutral-500">Loading...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-neutral-500">No loyalty balances yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-neutral-100">
                <td className="p-4 font-medium">{r.email}</td>
                <td className="p-4 text-right font-bold text-[#FF5A1F]">{r.balance || 0}</td>
                <td className="p-4 text-right text-xs">{r.total_earned || 0}</td>
                <td className="p-4 text-right text-xs">{r.total_redeemed || 0}</td>
                <td className="p-4 pr-6 text-right">
                  <button onClick={() => setAdjusting(r.email)} className="text-xs font-semibold text-[#FF5A1F] hover:underline flex items-center gap-1 ml-auto"><Coins className="w-3.5 h-3.5" /> Adjust</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adjusting && (
        <Modal onClose={() => setAdjusting(null)} title={`Adjust points — ${adjusting}`}>
          <p className="text-sm text-neutral-500">Use positive number to add points, negative to deduct.</p>
          <Field label="Points delta">
            <input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 100 or -50" className="input" data-testid="loy-delta" />
          </Field>
          <div className="mt-6 flex gap-2 justify-end">
            <button onClick={() => setAdjusting(null)} className="btn-secondary">Cancel</button>
            <button onClick={submitAdjust} className="btn-primary" data-testid="save-loy-btn">Apply</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
